import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Copy, MailCheck, MailWarning, ShieldCheck } from "lucide-react";
import { api, errMsg, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const CredentialsNotice = ({ result, onClose }) => {
  if (!result) return null;
  const copy = () => { navigator.clipboard.writeText(result.temporary_password); toast.success("Mot de passe copié"); };
  return (
    <div data-testid="invite-result" className={`border p-4 text-sm ${result.email_sent ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-300"}`}>
      {result.email_sent ? (
        <p className="flex items-center gap-2 font-semibold text-green-800"><MailCheck size={16} /> E-mail envoyé à {result.user?.email || "l'administrateur"} avec son mot de passe temporaire.</p>
      ) : (
        <>
          <p className="flex items-center gap-2 font-semibold text-amber-800"><MailWarning size={16} /> {result.mail_configured ? "L'envoi de l'e-mail a échoué." : "L'envoi d'e-mail n'est pas encore configuré (GMAIL_USER / GMAIL_APP_PASSWORD)."} Transmettez ce mot de passe manuellement :</p>
          <div className="flex items-center gap-2 mt-2">
            <code data-testid="invite-temporary-password" className="bg-white border px-3 py-1.5 font-mono text-base tracking-wider">{result.temporary_password}</code>
            <button onClick={copy} data-testid="invite-copy-password" className="inline-flex items-center gap-1 border px-3 py-1.5 text-xs font-bold hover:border-red-600 hover:text-red-600"><Copy size={12} /> Copier</button>
          </div>
        </>
      )}
      <button onClick={onClose} className="text-xs underline mt-2 text-zinc-600">Fermer</button>
    </div>
  );
};

const InviteForm = ({ onInvited }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success("Administrateur ajouté");
      setForm({ name: "", email: "" });
      onInvited(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 p-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="invite-form">
      <div className="space-y-1.5"><Label>Nom complet</Label><Input data-testid="invite-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
      <div className="space-y-1.5"><Label>E-mail</Label><Input data-testid="invite-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></div>
      <Button data-testid="invite-submit" disabled={loading} className="bg-red-600 hover:bg-red-700 rounded-none font-bold uppercase text-xs tracking-wider h-10"><UserPlus size={15} className="mr-1" /> {loading ? "Envoi…" : "Inviter"}</Button>
    </form>
  );
};

const PasswordForm = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Les deux mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await api.put("/auth/password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Mot de passe modifié");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="password-form">
      <h2 className="font-heading font-black text-lg border-l-4 border-red-600 pl-3 flex items-center gap-2"><ShieldCheck size={18} /> Mon mot de passe</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <Input data-testid="password-current" type="password" required placeholder="Mot de passe actuel" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="rounded-none" />
        <Input data-testid="password-new" type="password" required minLength={8} placeholder="Nouveau (8 caractères min.)" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-none" />
        <Input data-testid="password-confirm" type="password" required placeholder="Confirmer" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="rounded-none" />
      </div>
      <Button data-testid="password-submit" disabled={loading} variant="outline" className="rounded-none border-[#111827] font-bold uppercase text-xs tracking-wider hover:bg-[#111827] hover:text-white">{loading ? "…" : "Modifier"}</Button>
    </form>
  );
};

export default function UsersManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [result, setResult] = useState(null);
  const [mail, setMail] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/admin/mail-status").then((r) => setMail(r.data)).catch(() => {}); }, [load]);

  const reset = async (u) => {
    try { const { data } = await api.post(`/admin/users/${u.id}/reset-password`); setResult({ ...data, user: u }); toast.success("Nouveau mot de passe généré"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Administrateur supprimé"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-manager">
      <div>
        <h1 className="font-heading text-3xl font-black">Administrateurs</h1>
        <p className="text-zinc-500 text-sm mt-1">Invitez des membres de la rédaction : un mot de passe est généré et envoyé par e-mail.</p>
        {mail && <p data-testid="mail-status" className={`text-xs mt-2 inline-flex items-center gap-1 ${mail.configured ? "text-green-700" : "text-amber-700"}`}>{mail.configured ? <><MailCheck size={12} /> Envoi d'e-mails actif via {mail.sender}</> : <><MailWarning size={12} /> Envoi d'e-mails non configuré : le mot de passe s'affichera à l'écran</>}</p>}
      </div>
      <InviteForm onInvited={(data) => { setResult(data); load(); }} />
      <CredentialsNotice result={result} onClose={() => setResult(null)} />
      <div className="bg-white border border-zinc-200 divide-y">
        {users.map((u, i) => (
          <div key={u.id} data-testid={`user-row-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
            <div className="w-9 h-9 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-sm shrink-0">{(u.name || u.email)[0].toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{u.name} {u.id === me?.id && <span className="badge-cat ml-1">Vous</span>} {u.must_change_password && <span className="text-[10px] uppercase font-bold text-amber-600 ml-1">Mot de passe temporaire</span>}</div>
              <div className="text-xs text-zinc-500 truncate">{u.email} · ajouté le {formatDate(u.created_at)}{u.invited_by && ` par ${u.invited_by}`}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`user-reset-${i}`} onClick={() => reset(u)} className="inline-flex items-center gap-1 border px-3 py-2 text-xs font-bold uppercase hover:border-red-600 hover:text-red-600"><KeyRound size={13} /> Nouveau mot de passe</button>
              {u.id !== me?.id && <button data-testid={`user-delete-${i}`} onClick={() => del(u)} aria-label="Supprimer" className="border px-3 py-2 hover:bg-red-600 hover:text-white"><Trash2 size={13} /></button>}
            </div>
          </div>
        ))}
      </div>
      <PasswordForm />
    </div>
  );
}
